// src/encounter/procedure-add-dialog.tsx
// Procedure order dialog for the A/P canvas — composed on OrderDialogShell (PROD-1801).
//
// PARITY REBUILD, 2026-07-30. Ported from the LIVE Flutter dialog (dev-provider.scrips.com, captured
// 2026-07-30 — "Onc thyr mut alys 10 gen&37", 0245U) and Figma Encounter-2. Labs, radiology and
// procedure are the SAME dialog in Flutter; procedure differs only by its coding-system label and the
// add-to-billing checkbox, both of which ride the shell's `headerAside` and body slot.
//
// THE CODING SYSTEM IS A STATIC LABEL, NOT A DROPDOWN — the change Samer asked for on 2026-07-29:
// "no need for cpt as dropdown, procedure should not be changeable in this screen". The live Flutter
// dialog renders `CPT` as plain text at the top-right of the card; React had turned it into a
// CPT/SNOMED `Select`, which is the drift. It is now `headerAside` text.
//
// What that costs, stated rather than hidden: the system can no longer be switched IN this dialog.
// It is inferred from the dragged item (`system: 'cpt' | 'snomed'`) and otherwise defaults to CPT.
// That is not a regression in reach — the pre-existing code comment already recorded that SNOMED has
// no master-catalog fallback, so the SNOMED branch only ever worked when the dialog was seeded from a
// provider-scoped drag. A cold open searched master CPT either way. If clinicians do need to reach
// SNOMED procedures from a cold open, that is a catalog problem (a SNOMED master search), not a
// dropdown problem, and it should be its own ticket rather than smuggled back in here.
//
// Order id: picked code + `orderSeq` — no random/clock source, so the same procedure ordered twice
// (or against two Dx) still gets distinct, reproducible ids.
import { useState } from 'react';
import { CodeSearchField, type CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import { useProcedureSearch, type ProcedureSystem } from './services/use-procedure-search';
import { toProcedureOrder, type ProcedureOrder } from './lib/to-procedure-order';
import { OrderDialogShell } from './order-dialog-shell';
import type { DiagnosisOption } from './diagnosis-multi-select';
import { useDesignV2 } from './lib/design-v2';

/** The confirm payload, widened with the diagnoses the clinician linked (PROD-1801). */
export interface ProcedureConfirm extends ProcedureOrder {
  /** Every linked diagnosis, in canvas order — dxIds[0] is the nesting parent. */
  dxIds: string[];
}

export interface ProcedureAddDialogProps {
  /** Practitioner id for the provider catalog search (null → master CPT fallback). */
  providerId: string | null | undefined;
  /** The encounter's diagnoses, for the DIAGNOSIS block. */
  diagnoses: DiagnosisOption[];
  /** Pre-ticked diagnoses — the canvas seeds the Dx the picker was opened from. */
  initialDxIds?: readonly string[];
  /** Session sequence folded into the order id. */
  orderSeq?: number;
  /**
   * Pre-selected procedure (seeded from a dragged panel item). When present the procedure is already
   * picked AND its coding system is taken from the item, so a dragged procedure needs no re-search.
   */
  initialItem?: CodeSearchResult;
  /**
   * PROD-3184: search text to open with, and NO pick. Set only for an ambient procedure the server
   * matched to no CPT record (`ambientSearchText`): an uncoded procedure is not an order, so the
   * clinician picks the record. Ignored when `initialItem` is set.
   */
  initialQuery?: string;
  initialAddToBilling?: boolean;
  initialNote?: string;
  editing?: boolean;
  onConfirm: (order: ProcedureConfirm) => void;
  /**
   * Destructive DELETE, bottom-right — the red trash glyph in the Figma frames. Editing only (the
   * host passes its existing order-removal callback); omitted, nothing renders.
   */
  onDelete?: () => void;
  onCancel: () => void;
  /**
   * AUTHORING an order set (PROD-2714): the same form with no encounter. The DIAGNOSIS block and the
   * note are not shown (see OrderDialogShell `authoring`).
   */
  authoring?: boolean;
}

/** The coding system from a dragged item — else the CPT default (the only master-searchable one). */
function initialSystem(item?: CodeSearchResult): ProcedureSystem {
  const s = item?.system?.toLowerCase();
  return s === 'snomed' || s === 'cpt' ? s : 'cpt';
}

/** Stable order id from the picked code + the session sequence — no random/clock source. */
function makeOrderId(code: string | undefined, seq: number): string {
  return `proc-${code ?? 'x'}-${seq}`;
}

export function ProcedureAddDialog({
  diagnoses,
  initialDxIds,
  orderSeq = 0,
  initialItem,
  initialQuery,
  initialAddToBilling,
  initialNote,
  editing,
  onConfirm,
  onCancel,
  onDelete,
  authoring = false,
}: ProcedureAddDialogProps) {
  // Fixed for the life of the dialog — inferred from the drag, never switched here (see the header).
  const [system] = useState<ProcedureSystem>(() => initialSystem(initialItem));
  // Design sheet v1.1, switch ON only: the coding-system caption is a label, not an action.
  const designV2 = useDesignV2();
  const [query, setQuery] = useState(() => (initialItem ? '' : (initialQuery ?? '')));
  const [picked, setPicked] = useState<CodeSearchResult | null>(initialItem ?? null);
  // NO ADD-TO-BILLING CONTROL — Andrew, 31 Jul: "remove add to billing from the procedure as in
  // flutter", verified in the Dart before removing. procedure_add_widget.dart declares
  // `bool addToBilling = false` (:43), reads it from existing data (:67) and writes it back (:122) —
  // but binds it to NO widget. There is no checkbox, no onChanged, no setState. The React checkbox was
  // drift.
  //
  // The VALUE still round-trips, exactly as Flutter's does: seeded from the saved order and emitted
  // unchanged, so an addToBilling set elsewhere survives an edit here rather than being cleared. That is
  // why the persistence work stays (it rides isPrimary — patient_assessment_model.dart:725); only the
  // control goes.
  const addToBilling = initialAddToBilling ?? false;
  const [note, setNote] = useState(initialNote ?? '');
  const [dxIds, setDxIds] = useState<string[]>(() => [...(initialDxIds ?? [])]);

  // Global/master catalog (providerId omitted) so a procedure dragged from the A/P panel (which
  // searches master CPT) is findable here (PROD-1421; Codex #145).
  const { data: results = [], isFetching } = useProcedureSearch(undefined, system, query);

  function handleConfirm() {
    if (!picked) return;
    onConfirm({
      ...toProcedureOrder(picked, { system, addToBilling, note }, makeOrderId(picked.code, orderSeq)),
      dxIds,
    });
  }

  return (
    <OrderDialogShell
      authoring={authoring}
      title={editing ? 'Edit procedure' : 'Add procedure'}
      fieldLabel="Procedure"
      itemName={picked?.display || picked?.code || 'No procedure selected'}
      itemCode={picked?.code}
      // The coding system as STATIC TEXT, top-right — Flutter's anatomy. Never a control here.
      headerAside={
        <span className={designV2 ? 'text-size-sm font-medium text-text-secondary uppercase' : 'text-size-sm font-medium text-brand-primary uppercase'}>
          {system}
        </span>
      }
      diagnoses={diagnoses}
      selectedDxIds={dxIds}
      onDxChange={setDxIds}
      note={note}
      onNoteChange={setNote}
      // ONE SAVE LABEL across every A/P dialog (Andrew, 1 Aug). Flutter's shared add widget defaults to
      // S.of(context).save — "Save" — for all of them (base_ap_item_add_widget.dart:288-290). We had
      // grown THREE: "Save", "Confirm", and "Add to plan". The shell already defaults to Save, so the
      // per-dialog override just goes.
      // The shell already blocks a save with zero diagnoses; this adds "a procedure must be chosen".
      canSave={!!picked}
      onSave={handleConfirm}
      onCancel={onCancel}
      onDelete={onDelete}
      // Cold open keeps the search; seeded from a drag the procedure is fixed, per the frame's filled
      // state and Samer's "should not be changeable in this screen".
      searchSlot={
        initialItem ? undefined : (
          <CodeSearchField
            value={query}
            onChange={setQuery}
            results={results}
            loading={isFetching}
            placeholder={system === 'snomed' ? 'Search SNOMED…' : 'Search CPT procedures…'}
            onResultClick={setPicked}
          />
        )
      }
    >
    </OrderDialogShell>
  );
}
